import { cn } from '@/lib/utils';

/** Tracé exact de l'étoile de la charte (pointes arrondies), l'icône lucide a des pointes vives. */
const FILLED_STAR_PATH =
  'M32.8202 42.7C31.7602 42.7 30.4002 42.36 28.7002 41.36L22.7202 37.82C22.1002 37.46 20.9002 37.46 20.3002 37.82L14.3002 41.36C10.7602 43.46 8.68024 42.62 7.74024 41.94C6.82024 41.26 5.38024 39.52 6.32024 35.52L7.74024 29.38C7.90024 28.74 7.58024 27.64 7.10024 27.16L2.14024 22.2C-0.339765 19.72 -0.139764 17.6 0.200236 16.56C0.540236 15.52 1.62024 13.68 5.06024 13.1L11.4402 12.04C12.0402 11.94 12.9002 11.3 13.1602 10.76L16.7002 3.7C18.3002 0.48 20.4002 0 21.5002 0C22.6002 0 24.7002 0.48 26.3002 3.7L29.8202 10.74C30.1002 11.28 30.9602 11.92 31.5602 12.02L37.9402 13.08C41.4002 13.66 42.4802 15.5 42.8002 16.54C43.1202 17.58 43.3202 19.7 40.8602 22.18L35.9002 27.16C35.4202 27.64 35.1202 28.72 35.2602 29.38L36.6802 35.52C37.6002 39.52 36.1802 41.26 35.2602 41.94C34.7602 42.3 33.9602 42.7 32.8202 42.7Z';

const OUTLINED_STAR_PATH = `${FILLED_STAR_PATH}M21.5002 34.54C22.4802 34.54 23.4602 34.78 24.2402 35.24L30.2202 38.78C31.9602 39.82 33.0602 39.82 33.4802 39.52C33.9002 39.22 34.2002 38.16 33.7602 36.2L32.3402 30.06C31.9602 28.4 32.5802 26.26 33.7802 25.04L38.7402 20.08C39.7202 19.1 40.1602 18.14 39.9602 17.48C39.7402 16.82 38.8202 16.28 37.4602 16.06L31.0802 15C29.5402 14.74 27.8602 13.5 27.1602 12.1L23.6402 5.06C23.0002 3.78 22.2002 3.02 21.5002 3.02C20.8002 3.02 20.0002 3.78 19.3802 5.06L15.8402 12.1C15.1402 13.5 13.4602 14.74 11.9202 15L5.56024 16.06C4.20024 16.28 3.28024 16.82 3.06024 17.48C2.84024 18.14 3.30024 19.12 4.28024 20.08L9.24024 25.04C10.4402 26.24 11.0602 28.4 10.6802 30.06L9.26024 36.2C8.80024 38.18 9.12024 39.22 9.54024 39.52C9.96024 39.82 11.0402 39.8 12.8002 38.78L18.7802 35.24C19.5402 34.78 20.5202 34.54 21.5002 34.54Z`;

const Star = ({ filled, className }: { filled: boolean; className?: string }) => (
  <svg
    viewBox="0 0 42.9876 42.7"
    fill="currentColor"
    aria-hidden="true"
    className={cn('h-full w-full', className)}
  >
    <path d={filled ? FILLED_STAR_PATH : OUTLINED_STAR_PATH} />
  </svg>
);

interface StarRatingProps {
  value: number | null;
  onChange: (rating: number) => void;
  max?: number;
  disabled?: boolean;
  label?: string;
  className?: string;
}

export const StarRating = ({
  value,
  onChange,
  max = 5,
  disabled = false,
  label,
  className,
}: StarRatingProps) => (
  <div role="radiogroup" aria-label={label} className={cn('flex items-center gap-4', className)}>
    {Array.from({ length: max }, (_, index) => index + 1).map((rating) => {
      const isFilled = value !== null && value >= rating;
      return (
        <button
          key={rating}
          type="button"
          role="radio"
          aria-checked={value === rating}
          aria-label={String(rating)}
          disabled={disabled}
          onClick={() => onChange(rating)}
          className={cn(
            'size-10 shrink-0 rounded-full outline-none transition-transform sm:size-12',
            isFilled ? 'text-[#FFC609]' : 'text-input',
            disabled
              ? 'cursor-not-allowed'
              : 'cursor-pointer hover:scale-110 focus-visible:ring-2 focus-visible:ring-primary',
          )}
        >
          <Star filled={isFilled} />
        </button>
      );
    })}
  </div>
);
